import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useCategoryStore } from '@/stores/categoryStore';
import { BlahblahCard } from '@/components/BlahblahCard';
import * as usersApi from '@/api/users';
import * as blahblahsApi from '@/api/blahblahs';
import type { UserProfile, Blahblah } from '@/types';
import { cn, formatRelativeTime } from '@/lib/utils';
import { Bell, BellOff, Loader2 } from 'lucide-react';

type ProfileTab = 'blahblahs' | 'favorites' | 'followed';

export function ProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuthStore();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [blahblahs, setBlahblahs] = useState<Blahblah[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProfileTab>('blahblahs');

  // Favorites state
  const [favorites, setFavorites] = useState<Blahblah[]>([]);
  const [favoritesNextCursor, setFavoritesNextCursor] = useState<string | null>(null);
  const [favoritesHasMore, setFavoritesHasMore] = useState(true);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesLoadingMore, setFavoritesLoadingMore] = useState(false);

  const {
    followedThreads,
    followedLoading,
    followedLoadingMore,
    followedHasMore,
    followedError,
    loadFollowedThreads,
    loadMoreFollowedThreads,
    unfollowThread,
    toggleThreadNotifications,
  } = useCategoryStore();

  const isOwnProfile = currentUser?.id === userId;

  useEffect(() => {
    if (!userId) return;

    const loadProfile = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [profileData, blahblahsData] = await Promise.all([
          usersApi.getUserProfile(userId),
          blahblahsApi.getUserBlahblahs(userId),
        ]);

        setProfile(profileData);
        setBlahblahs(blahblahsData.items);
        setNextCursor(blahblahsData.nextCursor);
        setHasMore(blahblahsData.hasMore);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load profile';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [userId]);

  // Load followed threads when switching to that tab (only for own profile)
  useEffect(() => {
    if (isOwnProfile && activeTab === 'followed' && followedThreads.length === 0) {
      loadFollowedThreads();
    }
  }, [isOwnProfile, activeTab, followedThreads.length, loadFollowedThreads]);

  // Load favorites when switching to that tab (only for own profile)
  useEffect(() => {
    if (isOwnProfile && activeTab === 'favorites' && favorites.length === 0 && !favoritesLoading) {
      const loadFavorites = async () => {
        setFavoritesLoading(true);
        try {
          const response = await blahblahsApi.getFavorites();
          setFavorites(response.items);
          setFavoritesNextCursor(response.nextCursor);
          setFavoritesHasMore(response.hasMore);
        } catch {
          // Silently fail
        } finally {
          setFavoritesLoading(false);
        }
      };
      loadFavorites();
    }
  }, [isOwnProfile, activeTab, favorites.length, favoritesLoading]);

  const handleLoadMore = async () => {
    if (!userId || !hasMore || isLoadingMore || !nextCursor) return;

    setIsLoadingMore(true);

    try {
      const response = await blahblahsApi.getUserBlahblahs(userId, nextCursor);
      setBlahblahs((prev) => [...prev, ...response.items]);
      setNextCursor(response.nextCursor);
      setHasMore(response.hasMore);
    } catch {
      // Silently fail for load more
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLoadMoreFavorites = async () => {
    if (!favoritesHasMore || favoritesLoadingMore || !favoritesNextCursor) return;

    setFavoritesLoadingMore(true);

    try {
      const response = await blahblahsApi.getFavorites(favoritesNextCursor);
      setFavorites((prev) => [...prev, ...response.items]);
      setFavoritesNextCursor(response.nextCursor);
      setFavoritesHasMore(response.hasMore);
    } catch {
      // Silently fail for load more
    } finally {
      setFavoritesLoadingMore(false);
    }
  };

  const handleFollow = async () => {
    if (!profile) return;

    try {
      if (profile.isFollowing) {
        await usersApi.unfollowUser(profile.id);
        setProfile((prev) =>
          prev ? { ...prev, isFollowing: false, followersCount: prev.followersCount - 1 } : null
        );
      } else {
        await usersApi.followUser(profile.id);
        setProfile((prev) =>
          prev ? { ...prev, isFollowing: true, followersCount: prev.followersCount + 1 } : null
        );
      }
    } catch {
      // Handle error
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (activeTab === 'blahblahs' && hasMore && !isLoadingMore) {
        handleLoadMore();
      } else if (activeTab === 'favorites' && favoritesHasMore && !favoritesLoadingMore) {
        handleLoadMoreFavorites();
      } else if (activeTab === 'followed' && followedHasMore && !followedLoadingMore) {
        loadMoreFollowedThreads();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="text-center py-12 px-4">
        <p className="text-red-500">{error || 'User not found'}</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto" onScroll={handleScroll}>
      {/* Profile header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start gap-4">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
            style={{ backgroundColor: profile.profileColor || '#6366f1' }}
          >
            {profile.displayName.charAt(0).toUpperCase()}
          </div>

          <div className="flex-1">
            <h1 className="text-xl font-bold text-card-foreground">{profile.displayName}</h1>

            <div className="flex gap-4 mt-2 text-sm">
              <span>
                <strong>{profile.followersCount}</strong>{' '}
                <span className="text-muted-foreground">followers</span>
              </span>
              <span>
                <strong>{profile.followingCount}</strong>{' '}
                <span className="text-muted-foreground">following</span>
              </span>
              <span>
                <strong>{profile.blahblahsCount}</strong>{' '}
                <span className="text-muted-foreground">blahblahs</span>
              </span>
            </div>

            {!isOwnProfile && (
              <button
                onClick={handleFollow}
                className={
                  profile.isFollowing
                    ? 'mt-3 px-4 py-1.5 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors'
                    : 'mt-3 px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors'
                }
              >
                {profile.isFollowing ? 'Following' : 'Follow'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs (only for own profile) */}
      {isOwnProfile && (
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('blahblahs')}
            className={cn(
              'flex-1 py-3 text-sm font-medium transition-colors',
              activeTab === 'blahblahs'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            My Blahblahs
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={cn(
              'flex-1 py-3 text-sm font-medium transition-colors',
              activeTab === 'favorites'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Favorites
          </button>
          <button
            onClick={() => setActiveTab('followed')}
            className={cn(
              'flex-1 py-3 text-sm font-medium transition-colors',
              activeTab === 'followed'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Followed Threads
          </button>
        </div>
      )}

      {/* Blahblahs tab */}
      {activeTab === 'blahblahs' && (
        <>
          {blahblahs.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-muted-foreground">
                {isOwnProfile ? "You haven't posted any blahblahs yet" : 'No blahblahs yet'}
              </p>
            </div>
          ) : (
            <div>
              {blahblahs.map((blahblah) => (
                <BlahblahCard key={blahblah.id} blahblah={blahblah} />
              ))}
            </div>
          )}

          {isLoadingMore && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {!hasMore && blahblahs.length > 0 && (
            <p className="text-center text-muted-foreground text-sm py-4">
              You've reached the end
            </p>
          )}
        </>
      )}

      {/* Favorites tab */}
      {activeTab === 'favorites' && isOwnProfile && (
        <>
          {favoritesLoading && favorites.length === 0 && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {!favoritesLoading && favorites.length === 0 && (
            <div className="text-center py-12 px-4">
              <span className="text-4xl mb-4 block">⭐</span>
              <p className="text-muted-foreground">
                You haven't favorited any blahblahs yet.
              </p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Tap the star icon on blahblahs to save them here.
              </p>
            </div>
          )}

          {favorites.length > 0 && (
            <div>
              {favorites.map((blahblah) => (
                <BlahblahCard key={blahblah.id} blahblah={blahblah} />
              ))}
            </div>
          )}

          {favoritesLoadingMore && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {!favoritesHasMore && favorites.length > 0 && (
            <p className="text-center text-muted-foreground text-sm py-4">
              You've reached the end
            </p>
          )}
        </>
      )}

      {/* Followed threads tab */}
      {activeTab === 'followed' && isOwnProfile && (
        <>
          {followedError && (
            <div className="p-4 text-center">
              <p className="text-red-500 text-sm mb-2">{followedError}</p>
              <button
                onClick={() => loadFollowedThreads()}
                className="text-sm text-primary hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {followedLoading && followedThreads.length === 0 && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {!followedLoading && followedThreads.length === 0 && !followedError && (
            <div className="text-center py-12 px-4">
              <span className="text-4xl mb-4 block">🔔</span>
              <p className="text-muted-foreground">
                You're not following any threads yet.
              </p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Follow threads to get notified of new replies.
              </p>
            </div>
          )}

          {followedThreads.length > 0 && (
            <div className="divide-y divide-border">
              {followedThreads.map((thread) => (
                <div key={thread.blahblah.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/blahblah/${thread.blahblah.id}`}
                        className="font-medium text-card-foreground hover:text-primary transition-colors line-clamp-2"
                      >
                        {thread.blahblah.aiTitle || 'Untitled thread'}
                      </Link>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <span>by {thread.blahblah.user.displayName}</span>
                        <span>·</span>
                        <span>{formatRelativeTime(thread.followedAt)}</span>
                        {thread.newRepliesCount > 0 && (
                          <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                            {thread.newRepliesCount} new
                          </span>
                        )}
                      </div>
                      {thread.blahblah.categories && thread.blahblah.categories.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {thread.blahblah.categories.slice(0, 2).map((cat) => (
                            <Link
                              key={cat.id}
                              to={`/categories/${cat.slug}`}
                              className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full hover:bg-muted/80"
                            >
                              {cat.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleThreadNotifications(thread.blahblah.id, !thread.notificationsEnabled)}
                        className={cn(
                          'p-2 rounded-full transition-colors',
                          thread.notificationsEnabled
                            ? 'text-primary bg-primary/10 hover:bg-primary/20'
                            : 'text-muted-foreground hover:bg-muted'
                        )}
                        title={thread.notificationsEnabled ? 'Mute notifications' : 'Enable notifications'}
                      >
                        {thread.notificationsEnabled ? (
                          <Bell className="w-4 h-4" />
                        ) : (
                          <BellOff className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => unfollowThread(thread.blahblah.id)}
                        className="px-3 py-1 text-xs font-medium text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-full transition-colors"
                      >
                        Unfollow
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {followedLoadingMore && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {!followedHasMore && followedThreads.length > 0 && (
            <p className="text-center text-muted-foreground text-sm py-4">
              You've reached the end
            </p>
          )}
        </>
      )}
    </div>
  );
}
